import { useState } from "react";

export default function Contact() {
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // Honeypot check
    const honeypot = String(fd.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    const message = String(fd.get("message") || "");
    if (message.length > 500) {
      setStatus("error");
      setErrorMsg("お問い合わせ内容は500文字以内でご記入ください。");
      return;
    }

    // Build form-urlencoded payload
    const params = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key === "website_alt") return; // exclude empty honeypot
      params.append(key, String(value));
    });

    setSubmitting(true);
    setStatus("idle");
    setErrorMsg("");
    try {
      const res = await fetch("https://readdy.ai/api/form/d8ug7pb9akq3s3aso90g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      if (!res.ok) throw new Error("send failed");
      setStatus("success");
      form.reset();
    } catch (err) {
      setStatus("error");
      setErrorMsg("送信に失敗しました。時間をおいて再度お試しください。");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      role="region"
      aria-label="お問い合わせ"
      className="relative bg-foreground-950 py-20 md:py-32 px-6 md:px-16 lg:px-24 overflow-hidden"
    >
      <div className="absolute inset-0 opacity-30">
        <img
          src="https://www.stylekoubou.com/image/top/top_image.webp"
          alt=""
          className="w-full h-full object-cover object-top"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-foreground-950 via-foreground-950/90 to-foreground-950"></div>

      <div className="relative max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-16">
        {/* Left text */}
        <div className="lg:col-span-2">
          <div className="inline-flex items-center gap-3 mb-4">
            <span className="w-10 h-px bg-primary-400"></span>
            <span className="text-xs tracking-[0.3em] uppercase text-primary-300 font-medium">
              Contact
            </span>
          </div>
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-background-50 leading-tight mb-6">
            まずは、<br />
            お気軽に<br />
            <span className="text-primary-300">ご相談ください</span>。
          </h2>
          <p className="text-background-50/70 text-base leading-relaxed mb-10">
            お見積もりは無料です。簡単なヒアリングのあと、最短即日で
            概算をお伝えします。
          </p>

          <div className="space-y-5">
            <a
              href="tel:0363792823"
              className="flex items-center gap-4 p-5 rounded-2xl bg-background-50/5 border border-background-50/10 hover:border-primary-400/50 hover:bg-background-50/10 transition-colors cursor-pointer"
            >
              <span className="w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center shrink-0">
                <i className="ri-phone-line text-background-50 text-xl"></i>
              </span>
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase text-background-50/60 mb-1">Tel</p>
                <p className="font-heading text-xl md:text-2xl font-bold text-background-50">
                  03-6379-2823
                </p>
                <p className="text-xs text-background-50/60">受付 9:00 - 18:00(水・日・祝休)</p>
              </div>
            </a>
            <div className="flex items-center gap-4 p-5 rounded-2xl bg-background-50/5 border border-background-50/10">
              <span className="w-12 h-12 rounded-full bg-accent-500 flex items-center justify-center shrink-0">
                <i className="ri-mail-line text-background-50 text-xl"></i>
              </span>
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase text-background-50/60 mb-1">Mail</p>
                <p className="font-heading text-base md:text-lg font-bold text-background-50">
                  info@wind-service0120.com
                </p>
                <p className="text-xs text-background-50/60">24時間受付・営業日にご返信</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right form */}
        <div className="lg:col-span-3">
          <form
            id="wind-contact-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="bg-background-50 rounded-2xl p-6 md:p-10"
          >
            <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-2">
              お問い合わせフォーム
            </h3>
            <p className="text-sm text-foreground-600 mb-6">
              <span className="text-accent-600">*</span> は必須項目です。
            </p>

            {/* Honeypot */}
            <input
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="wind-hp-field"
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5 mb-4 md:mb-5">
              <div>
                <label className="block text-xs font-semibold text-foreground-800 mb-2 tracking-wide">
                  お名前 <span className="text-accent-600">*</span>
                </label>
                <input
                  required
                  type="text"
                  name="name"
                  placeholder="山田 太郎"
                  className="w-full text-sm px-4 py-3 rounded-md border border-background-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition-all bg-background-50 text-foreground-900"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground-800 mb-2 tracking-wide">
                  会社名・屋号
                </label>
                <input
                  type="text"
                  name="company"
                  placeholder="株式会社○○"
                  className="w-full text-sm px-4 py-3 rounded-md border border-background-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition-all bg-background-50 text-foreground-900"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5 mb-4 md:mb-5">
              <div>
                <label className="block text-xs font-semibold text-foreground-800 mb-2 tracking-wide">
                  メールアドレス <span className="text-accent-600">*</span>
                </label>
                <input
                  required
                  type="email"
                  name="email"
                  placeholder="example@email.com"
                  className="w-full text-sm px-4 py-3 rounded-md border border-background-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition-all bg-background-50 text-foreground-900"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground-800 mb-2 tracking-wide">
                  電話番号
                </label>
                <input
                  type="tel"
                  name="phone"
                  placeholder="090-0000-0000"
                  className="w-full text-sm px-4 py-3 rounded-md border border-background-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition-all bg-background-50 text-foreground-900"
                />
              </div>
            </div>

            <div className="mb-4 md:mb-5">
              <label className="block text-xs font-semibold text-foreground-800 mb-2 tracking-wide">
                ご相談内容 <span className="text-accent-600">*</span>
              </label>
              <select
                required
                name="category"
                defaultValue=""
                className="w-full text-sm px-4 py-3 rounded-md border border-background-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition-all bg-background-50 text-foreground-900 cursor-pointer"
              >
                <option value="" disabled>選択してください</option>
                <option value="原状回復工事">原状回復工事</option>
                <option value="内装リフォーム">内装リフォーム</option>
                <option value="リノベーション">リノベーション</option>
                <option value="外壁塗装">外壁塗装</option>
                <option value="入居中修繕">入居中修繕</option>
                <option value="電気工事">電気工事</option>
                <option value="設備工事">設備工事</option>
                <option value="ハウスクリーニング">ハウスクリーニング</option>
                <option value="エアコン洗浄">エアコン洗浄</option>
                <option value="その他">その他</option>
              </select>
            </div>

            <div className="mb-6">
              <label className="block text-xs font-semibold text-foreground-800 mb-2 tracking-wide">
                お問い合わせ内容 <span className="text-accent-600">*</span>
              </label>
              <textarea
                required
                name="message"
                rows={4}
                maxLength={500}
                placeholder="物件の住所、希望工期、ご予算など、わかる範囲でご記入ください。"
                className="w-full text-sm px-4 py-3 rounded-md border border-background-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition-all bg-background-50 text-foreground-900 resize-none"
              ></textarea>
              <p className="text-xs text-foreground-500 mt-1.5 text-right">最大500文字</p>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-background-50 dark:text-foreground-950 font-semibold tracking-wide transition-colors cursor-pointer whitespace-nowrap"
            >
              {submitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>送信中...
                </>
              ) : (
                <>
                  この内容で送信する
                  <i className="ri-arrow-right-line"></i>
                </>
              )}
            </button>

            {status === "success" && (
              <div className="mt-4 p-4 rounded-md bg-primary-50 border border-primary-200 text-primary-800 text-sm flex items-center gap-2">
                <i className="ri-check-double-line text-primary-600"></i>
                お問い合わせありがとうございます。営業日にご返信いたします。
              </div>
            )}
            {status === "error" && (
              <div className="mt-4 p-4 rounded-md bg-accent-50 border border-accent-200 text-accent-800 text-sm flex items-center gap-2">
                <i className="ri-error-warning-line text-accent-600"></i>
                {errorMsg}
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}